import React, { useCallback, useState } from 'react';
import { graphql, useFragment } from 'react-relay';
import { Box, Flex, xcss, Text, Stack } from '@atlaskit/primitives';
import Heading from '@atlaskit/heading';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import type { reportCard_reportsOverviewPage$key } from '@atlassian/jira-relay/src/__generated__/reportCard_reportsOverviewPage.graphql';
import { ReportImage } from '../report-image/index.tsx';

type ReportCardProps = {
	report: reportCard_reportsOverviewPage$key;
};

export const ReportCard = ({ report }: ReportCardProps) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [isHovered, setIsHovered] = useState(false);

	const handleOnHoverEnter = () => {
		setIsHovered(true);
	};

	const handleOnHoverLeave = () => {
		setIsHovered(false);
	};

	const reportCardData = useFragment<reportCard_reportsOverviewPage$key>(
		graphql`
			fragment reportCard_reportsOverviewPage on JiraReport {
				...reportImage_reportsOverviewPage
				name @required(action: THROW)
				description @required(action: THROW)
				url @required(action: THROW)
			}
		`,
		report,
	);

	const name = reportCardData.name;
	const description = reportCardData.description;
	const url = reportCardData.url;

	const fireReportClickedAnalytics = useCallback(() => {
		fireUIAnalytics(createAnalyticsEvent({}), 'button clicked', 'reportButton', {
			report: name,
		});
	}, [createAnalyticsEvent, name]);

	const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
		if (event.key === 'Enter') {
			globalThis.window.location.href = url;
			fireReportClickedAnalytics();
		}
	};

	const handleClick = useCallback(() => {
		globalThis.window.location.href = url;
		fireReportClickedAnalytics();
	}, [fireReportClickedAnalytics, url]);

	return (
		<Flex xcss={cardContainerStyles} direction="column">
			<Box
				xcss={hoverCardStyles}
				padding="space.150"
				onMouseEnter={handleOnHoverEnter}
				onMouseLeave={handleOnHoverLeave}
				onClick={handleClick}
				onKeyDown={handleKeyDown}
				tabIndex={0}
				role="button"
			>
				<Stack space="space.100">
					<ReportImage report={reportCardData} />
					<Box xcss={isHovered ? headerHoverStyles : headerStyles}>
						<Heading size="xsmall" as="h3">
							{name}
						</Heading>
					</Box>
					<Text size="medium" as="p">
						{description}
					</Text>
				</Stack>
			</Box>
		</Flex>
	);
};

const headerHoverStyles = xcss({
	textDecoration: 'underline',
	color: 'color.link.pressed',
});

const headerStyles = xcss({
	textDecoration: 'none',
});

const hoverCardStyles = xcss({
	height: '320px',
	width: '270px',
	overflowY: 'auto',
	overflowX: 'hidden',
	':focus': {
		outline: 'none',
	},
});

const cardContainerStyles = xcss({
	height: '320px',
	width: '270px',
	overflow: 'hidden',
	position: 'relative',
	borderRadius: 'border.radius.200',
	border: '2px solid',
	borderColor: 'color.border',
	backgroundColor: 'elevation.surface.overlay',
	':hover': {
		borderColor: 'color.border.focused',
		borderStyle: 'solid',
		borderWidth: 'space.025',
	},
	':focus-within': {
		borderColor: 'color.border.focused',
		borderStyle: 'solid',
		borderWidth: 'space.025',
	},
	cursor: 'pointer',
});
